const MAX_DEPTH = 4;
const MAX_SERIALIZED_LENGTH = 400;
const MAX_NODES = 2000;

const REDACTED = "[redacted]";

// Thrown payloads reach Datadog, where they may be indexed, so keys that name a secret or a
// personal identifier keep their name but lose their value.
const SENSITIVE_WORDS = new Set([
	"auth",
	"authorization",
	"token",
	"password",
	"passwd",
	"passcode",
	"secret",
	"credential",
	"credentials",
	"cookie",
	"email",
	"phone",
	"ssn",
	"card",
	"cvv",
	"cvc",
	"jwt",
	"bearer",
	"otp",
	"pin",
	"iban",
]);

const SENSITIVE_FRAGMENTS = [
	"password",
	"passwd",
	"passcode",
	"secret",
	"token",
	"authorization",
	"authentication",
	"credential",
	"apikey",
	"privatekey",
	"accesskey",
	"secretkey",
];

type ThrownValueDescription = {
	thrownType: string;
	thrownConstructor: string;
	thrownValue: string;
};

type ErrorShape = {
	name: string;
	message: string;
	stack: string | undefined;
};

type NodeBudget = {
	remaining: number;
};

const isSensitiveKey = (key: string): boolean => {
	const words = key
		.replace(/([a-z0-9])([A-Z])/g, "$1 $2")
		.replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
		.toLowerCase()
		.split(/[\s_.-]+/);

	if (words.some((word) => SENSITIVE_WORDS.has(word))) {
		return true;
	}

	const collapsed = key.toLowerCase().replace(/[\s_.-]+/g, "");
	return SENSITIVE_FRAGMENTS.some((fragment) => collapsed.includes(fragment));
};

// `instanceof` walks the prototype chain, which throws for a revoked or trapping proxy.
const isError = (value: unknown): value is Error => {
	try {
		return value instanceof Error;
	} catch {
		return false;
	}
};

const readStringProperty = ({ value, key }: { value: object; key: string }): string | undefined => {
	try {
		const property = (value as Record<string, unknown>)[key];
		return typeof property === "string" ? property : undefined;
	} catch {
		return undefined;
	}
};

// Cross-realm errors fail `instanceof Error` and have no enumerable keys, so match on shape.
// A string `stack` keeps ordinary `{name, message}` payloads out of this branch, which would
// otherwise discard their remaining keys.
const errorShapeOf = (value: unknown): ErrorShape | undefined => {
	if (typeof value !== "object" || value === null) {
		return undefined;
	}

	const name = readStringProperty({ value, key: "name" });
	const message = readStringProperty({ value, key: "message" });

	if (name === undefined || message === undefined) {
		return undefined;
	}

	const stack = readStringProperty({ value, key: "stack" });
	const hasErrorShape = isError(value) || stack !== undefined;

	return hasErrorShape ? { name, message, stack } : undefined;
};

// Covers same-realm and cross-realm errors alike — both normalize to a real Error that already
// carries the original name and message, so their payload needs no separate description.
export const isErrorLike = (value: unknown): boolean => isError(value) || errorShapeOf(value) !== undefined;

const constructorNameOf = (value: object): string => {
	try {
		return Object.getPrototypeOf(value)?.constructor?.name ?? "Object";
	} catch {
		return "Object";
	}
};

const thrownConstructorName = (value: unknown): string => {
	if (value === null) {
		return "null";
	}

	if (value === undefined) {
		return "undefined";
	}

	if (typeof value === "object" || typeof value === "function") {
		return constructorNameOf(value);
	}

	return constructorNameOf(Object(value));
};

// `path` holds only the ancestors of the current node, so a value repeated across
// sibling branches still serializes in full — only a true cycle is [Circular].
const toSerializable = ({
	value,
	depth,
	path,
	budget,
}: {
	value: unknown;
	depth: number;
	path: readonly object[];
	budget: NodeBudget;
}): unknown => {
	budget.remaining -= 1;

	if (value === null) {
		return null;
	}

	switch (typeof value) {
		case "undefined":
			return "[undefined]";
		case "string":
		case "boolean":
			return value;
		case "number":
			return Number.isFinite(value) ? value : String(value);
		case "bigint":
			return `${value}n`;
		case "symbol":
			return value.toString();
		case "function":
			return `[Function: ${value.name || "anonymous"}]`;
	}

	if (budget.remaining <= 0) {
		return "[truncated]";
	}

	if (path.includes(value)) {
		return "[Circular]";
	}

	const errorShape = errorShapeOf(value);
	if (errorShape) {
		return { name: errorShape.name, message: errorShape.message };
	}

	if (value instanceof Date) {
		return value.toISOString();
	}

	if (typeof Node !== "undefined" && value instanceof Node) {
		return `[${constructorNameOf(value)}]`;
	}

	if (depth >= MAX_DEPTH) {
		return `[${constructorNameOf(value)}]`;
	}

	const nextPath = [...path, value];

	if (Array.isArray(value)) {
		return value.map((item) => {
			try {
				return toSerializable({ value: item, depth: depth + 1, path: nextPath, budget });
			} catch {
				return "[unreadable]";
			}
		});
	}

	const keys = Object.keys(value);
	if (keys.length === 0) {
		return `[${constructorNameOf(value)}]`;
	}

	const entries: Record<string, unknown> = Object.create(null);
	for (const key of keys) {
		if (isSensitiveKey(key)) {
			entries[key] = REDACTED;
			continue;
		}

		try {
			entries[key] = toSerializable({
				value: (value as Record<string, unknown>)[key],
				depth: depth + 1,
				path: nextPath,
				budget,
			});
		} catch {
			entries[key] = "[unreadable]";
		}
	}

	return entries;
};

export const safeStringify = (value: unknown): string => {
	let serialized: string | undefined;

	try {
		serialized = JSON.stringify(toSerializable({ value, depth: 0, path: [], budget: { remaining: MAX_NODES } }));
	} catch {
		return "[unserializable]";
	}

	if (serialized === undefined) {
		return "[unserializable]";
	}

	return serialized.length > MAX_SERIALIZED_LENGTH
		? `${serialized.slice(0, MAX_SERIALIZED_LENGTH)}...[truncated]`
		: serialized;
};

export const describeThrownValue = (error: unknown): ThrownValueDescription => ({
	thrownType: typeof error,
	thrownConstructor: thrownConstructorName(error),
	thrownValue: safeStringify(error),
});

// For a genuinely unrecognizable throw the message stays constant per kind so Datadog Error
// Tracking groups them; the variable payload travels in the capture context instead.
export const normalizeError = (error: unknown): Error => {
	if (isError(error)) {
		return error;
	}

	if (typeof error === "string") {
		return new Error(error);
	}

	const errorShape = errorShapeOf(error);
	if (errorShape) {
		const normalized = new Error(errorShape.message);
		normalized.name = errorShape.name;
		if (errorShape.stack !== undefined) {
			normalized.stack = errorShape.stack;
		}
		return normalized;
	}

	return new Error(`Non-Error thrown: ${thrownConstructorName(error)}`);
};
